import { Link, useLocation, useParams } from 'react-router-dom';
import { Skeleton } from '@moneyskyline/ui';
import { AmountText } from '@/components/AmountText';
import { HonestInline } from '@/components/HonestInline';
import { useShellContext } from '@/app/shell/AppShell';
import { scopeHref } from '@/app/shell/scope';
import { useEventPacket, type EventPacketResult } from '@/api/hooks/useEventPacket';
import { decimalSum } from '@/features/transactions/decimal';
import { BusinessNav } from './BusinessNav';
import { RecordsDisclaimer } from './RecordsDisclaimer';

const SECTIONS = ['travel', 'lodging', 'meals', 'ground', 'other'] as const;
const SECTION_LABEL: Record<(typeof SECTIONS)[number], string> = {
  travel: 'Travel',
  lodging: 'Lodging',
  meals: 'Meals',
  ground: 'Ground',
  other: 'Other',
};

function receiptLabel(value: string): string {
  if (value === 'attached') return '✓ attached';
  if (value === 'missing') return 'missing · add';
  if (value === 'shared') return 'shares a receipt';
  return 'in-app receipt';
}

type ReadyPacket = Extract<EventPacketResult, { status: 'ready' }>;

function PacketBody({ packet, desktop }: { packet: ReadyPacket; desktop: boolean }) {
  const { search } = useLocation();
  const missing = packet.items.filter((item) => item.receipt === 'missing');
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-[14px] bg-foreground p-5 text-background">
        <div className="text-[12px] text-primary-foreground-muted">{packet.source_label ?? 'Event'}</div>
        <div className="mt-1 text-title font-semibold">{packet.name}</div>
        <div className="mt-1 text-sm text-primary-foreground-muted">
          {packet.date_label}
          {packet.location ? ` · ${packet.location}` : ''}
        </div>
        <div className="mt-4 flex items-baseline justify-between">
          <AmountText value={packet.total} currency={packet.currency} className="text-figure font-semibold text-background" variant="balance" />
          <span className="text-sm text-primary-foreground-muted">
            {packet.item_count} items · {packet.charge_count} charges
            {packet.pending_count ? ` · ${packet.pending_count} pending` : ''}
          </span>
        </div>
        {packet.condition_label && (
          <div className="mt-3 text-[12px] text-primary-foreground-muted">
            Business by condition {packet.condition_label}
          </div>
        )}
      </div>

      <div className={desktop ? 'grid grid-cols-[minmax(0,1fr)_360px] gap-6' : 'flex flex-col gap-4'}>
      <div className="overflow-hidden rounded-[10px] border border-border bg-card text-sm">
        {SECTIONS.map((section) => {
          const rows = packet.items.filter((item) => item.section === section);
          const sectionTotal = rows.length > 0 ? decimalSum(rows.map((row) => row.amount)) : null;
          return (
            <div key={section}>
              <div className="flex justify-between bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
                <span>{SECTION_LABEL[section]}{section === 'meals' ? ' · recorded at 100%' : ''}</span>
                {rows.length === 0 ? <span>none</span> : <AmountText value={sectionTotal} currency={packet.currency} />}
              </div>
              {rows.map((item) => (
                <Link
                  key={item.transaction_id}
                  to={scopeHref(`/transactions/item/${item.transaction_id}`, search)}
                  className="flex min-h-11 items-center justify-between border-t border-border px-3 text-foreground"
                >
                  <span>
                    {item.merchant}
                    <span className="text-[12px] text-muted-foreground">
                      {' '}· {item.date_label}
                      {item.account_last4 ? ` · •${item.account_last4}` : ''}
                      {item.pending ? ' · pending' : ''}
                      {item.note ? ` · ${item.note}` : ''}
                    </span>
                  </span>
                  <AmountText value={item.amount} currency={packet.currency} />
                </Link>
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex flex-col gap-4">

      {missing.length > 0 && (
        <div className="rounded-[10px] border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground">
          <div className="font-semibold">Missing receipts · {missing.length}</div>
          {missing.map((item) => (
            <div key={item.transaction_id} className="mt-1 flex min-h-9 items-center justify-between">
              <span>{item.merchant} · {item.date_label}</span>
              <span className="text-[12px]">{receiptLabel(item.receipt)}</span>
            </div>
          ))}
          <p className="mt-2 text-[12px]">Export still includes them; the checklist flags them. A suggestion never changes the total until added.</p>
        </div>
      )}

      {packet.likely_related.length > 0 && (
        <div className="rounded-[10px] border border-border bg-card p-3 text-sm" data-testid="likely-related-tray">
          <div className="flex items-center gap-1.5 font-semibold">
            <span aria-hidden className="h-2 w-2 rounded-full border-2 border-confidence-low box-border" />
            Likely related · {packet.likely_related.length}
          </div>
          <p className="mt-1 text-[12px] text-muted-foreground">Not in the total until you add it.</p>
          {packet.likely_related.map((item) => (
            <div key={item.transaction_id} className="mt-1 flex min-h-11 items-center justify-between">
              <span>
                {item.merchant}
                <span className="text-[12px] text-muted-foreground">
                  {' '}· {item.date_label}
                  {item.account_last4 ? ` · •${item.account_last4}` : ''}
                </span>
              </span>
              <AmountText value={item.amount} currency={packet.currency} />
            </div>
          ))}
        </div>
      )}
      </div>
      </div>
    </div>
  );
}

export function EventPage({ eventId: eventIdProp }: { eventId?: string } = {}) {
  const { eventId: eventIdParam } = useParams<{ eventId: string }>();
  const eventId = eventIdProp ?? eventIdParam;
  const { search } = useLocation();
  const { band } = useShellContext();
  const packetQuery = useEventPacket(eventId);
  const packet = packetQuery.data;
  const heading = packet?.status === 'ready' ? packet.name : 'Event';

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-6" aria-labelledby="event-heading">
      <Link to={scopeHref('/business', search)} className="text-sm text-link">‹ Business</Link>
      <h1 id="event-heading" tabIndex={-1} className="text-title font-semibold">{heading}</h1>
      <BusinessNav eventCount={packet?.status === 'ready' ? 1 : null} eventHref={eventId ? `/events/${eventId}` : null} />

      {packetQuery.isLoading && <Skeleton className="mt-4 h-40 w-full" />}
      {packet?.status === 'unavailable' && (
        <div className="mt-4" data-testid="event-unavailable">
          <HonestInline kind="event-unavailable" />
        </div>
      )}
      {packet?.status === 'empty' && (
        <p role="status" className="mt-4 text-sm text-muted-foreground">This event has no items yet.</p>
      )}
      {packet?.status === 'ready' && (
        <div className="mt-4">
          <PacketBody packet={packet} desktop={band === 'desktop'} />
        </div>
      )}
      <RecordsDisclaimer className="mt-6 max-w-xl" />
    </section>
  );
}

export default EventPage;
